import { useAuth } from 'hooks';
import { useCallback } from 'react';
import { ImageType, LabellingMode, Image } from 'redux/annotations/annotations.type';
import { useRedux, useReduxSelector } from 'redux/store.hooks';
import { useImageNavigation } from './useImageNavigation';

// Base editability of the current image, ignoring the windshield repair/replace lock.
// This determines who is allowed to touch the image at all (assigned data ops, analysed, etc.).
const useCanEditImageBase = (photoSeriesId: string | undefined, imageId: string | undefined, labellingMode: LabellingMode) => {
  const { email: currentUserEmail } = useAuth();
  const { annotations: { selectors: annotationsSelectors } } = useRedux();
  const photoSeries = useReduxSelector((state) => annotationsSelectors.selectPhotoSeriesByPhotoSeriesId(state, photoSeriesId as string));
  const { imageIndex } = useImageNavigation();
  const allImages = useReduxSelector((state) => annotationsSelectors.selectImagesByPhotoSeriesId(state, photoSeriesId as string));

  const currentImage =
    useReduxSelector((state) => annotationsSelectors.selectImageByImageIdAndPhotoSeriesId(state, photoSeriesId as string, imageId as string));

  const images = currentImage?.imageType == ImageType.Additional
    ? allImages?.filter((image: Image) => image.imageType === ImageType.Additional)
    : allImages?.filter((image: Image) => image.imageType !== ImageType.Additional);

  const imageNotAnalaysed = !images || !images[imageIndex] || !images[imageIndex].analysedAt;

  const canEditBase = useCallback(() => {
    if (photoSeries === undefined) return false;
    if (imageNotAnalaysed) return false;
    if (labellingMode === LabellingMode.ModelTraining) return true;

    return photoSeries.analysisAssignedToUser === currentUserEmail;
  }, [photoSeries, currentUserEmail, labellingMode, imageNotAnalaysed]);

  return { photoSeries, canEditBase };
};

const useCanEditCurrentImage = (photoSeriesId: string | undefined, imageId: string | undefined, labellingMode: LabellingMode) => {
  const { photoSeries, canEditBase } = useCanEditImageBase(photoSeriesId, imageId, labellingMode);

  return useCallback(() => {
    // Photo series is in windshield repair/replace decision review: body parts and
    // damages are locked, data ops only sets the repair decision.
    if (photoSeries?.windshieldRepairReplaceDecisionEnabled) return false;

    return canEditBase();
  }, [photoSeries, canEditBase]);
};

// Blur regions may be created/edited even during windshield repair/replace review,
// so this intentionally ignores the windshield lock that useCanEditCurrentImage applies.
const useCanEditBlurRegions = (photoSeriesId: string | undefined, imageId: string | undefined, labellingMode: LabellingMode) => {
  const { canEditBase } = useCanEditImageBase(photoSeriesId, imageId, labellingMode);
  return canEditBase;
};

export { useCanEditCurrentImage, useCanEditBlurRegions };
